import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, Subject } from 'rxjs';
import { tap, take, map, mergeMap, mergeAll, debounceTime } from 'rxjs/operators';
import { AnalyticsApiService, BackendService, Dataset, Match, Schema, SessionService } from '@hub-components';
import { DataSliceEditor, DataSliceViewer, DataSlicersViewData } from '../models/data-slicer.model';
import { Dashboard } from '../models/dashboard.model';
import { FormArray, FormBuilder } from '@angular/forms';

@Injectable({
  providedIn: 'root',
})
export class DataSlicersService {
  private dataSlicersSelectionsMap = new Map<number, any[]>();
  private dataSlicersDimensionsMap = new Map<number, DataSliceViewer[]>();
  private dataSlicersForm = this.fb.array([]);
  private loadedDataSlicersDropdowns: Map<number, Set<number>> = new Map();
  private dataSliceFilterSubject = new Subject<{ filter: string; dataSlice: any; index: number; pageId: number; shareId?: string }>();
  private dataSlicersViewData: DataSliceViewer[] = [];
  private dataSlicersViewDataSubject = new BehaviorSubject<DataSlicersViewData>({ pageId: null, dataSlicers: [] });
  private dataSlicersSchemas: { [dataset: string]: Schema } = {};
  private dataSlicersEditorMap = new Map<number, DataSliceEditor[]>();

  constructor(
    private analyticsApiService: AnalyticsApiService,
    private backendService: BackendService,
    private sessionService: SessionService,
    private fb: FormBuilder,
  ) {}

  public getDataSlicersSelections(pageId: number): any[] {
    return this.dataSlicersSelectionsMap.get(pageId);
  }

  public setDataSlicersSelections(pageId: number, selections: any[]): void {
    this.dataSlicersSelectionsMap.set(pageId, selections);
  }

  public getDataSlicersDimensions(pageId: number): DataSliceViewer[] | undefined {
    return this.dataSlicersDimensionsMap.get(pageId);
  }

  public setDataSlicersDimensions(pageId: number, dimensions: DataSliceViewer[]): void {
    this.dataSlicersDimensionsMap.set(pageId, dimensions);
  }

  public dataSlicerFilterInit(): Observable<any> {
    return this.dataSliceFilterSubject.pipe(
      debounceTime(500),
      tap(({ filter, dataSlice, index, pageId, shareId }) => {
        const pageSlicers = this.getDataSlicersDimensions(pageId) || [];
        if (pageSlicers[index]) {
          pageSlicers[index].loading = true;
          pageSlicers[index].matches = [];
        }

        this.setDataSlicersDimensions(pageId, pageSlicers);
        this.fetchSliceOptions(dataSlice, index, pageId, shareId, filter);
      }),
    );
  }

  public filterDataSlice(filter: string, dataSlice: any, index: number, pageId: number, shareId?: string): void {
    this.dataSliceFilterSubject.next({ filter, dataSlice, index, pageId, shareId });
  }

  public get dataSlicersViewData$(): Observable<DataSlicersViewData> {
    return this.dataSlicersViewDataSubject.asObservable();
  }

  public get dataSlicersFormValue(): any[] {
    return this.dataSlicersForm.getRawValue();
  }

  public get dataSlicersForm$(): FormArray {
    return this.dataSlicersForm;
  }

  public setDataSlicers(dataSlicers: DataSliceEditor[]): void {
    this.dataSlicersEditorMap.clear();

    dataSlicers.forEach(slice => {
      if (!this.dataSlicersEditorMap.has(slice.page)) {
        this.dataSlicersEditorMap.set(slice.page, []);
      }

      const existingSlices = this.dataSlicersEditorMap.get(slice.page);
      existingSlices?.push(slice);
    });
  }

  public getDataSlicers(): DataSliceEditor[] {
    return Array.from(this.dataSlicersEditorMap.values()).flat();
  }

  public getDataSlice(page: number): DataSliceEditor[] | undefined {
    return this.dataSlicersEditorMap.get(page);
  }

  public updateDataSlicers(page: number, dataSlicers: DataSliceEditor[]): void {
    this.dataSlicersEditorMap.set(page, dataSlicers);
  }

  public removeDataSlice(page: number) {
    this.dataSlicersEditorMap.delete(page);
  }

  public fetchSliceOptions(dataSlice: DataSliceViewer, index: number, pageId: number, shareId?: string, filter = ''): void {
    const params = { filter };
    if (this.sessionService.publicId) {
      params['public_id'] = this.sessionService.publicId;
    }
    if (shareId) {
      params['share_id'] = shareId;
    }

    this.backendService.load(`data_slicers/${dataSlice.id}/options`, {
      params,
      skipLoader: true,
    }).pipe(
      take(1),
      map((response: any) => response?.matches || []),
      map((rawMatches) => {
        return rawMatches.map((rawMatch) => {
          const match = rawMatch.match;
          const slices = rawMatch.slices;
          const slice = slices[0]['slice'][dataSlice.dimension.name];
          return new Match(slice, match, slices);
        });
      }),
      tap((matches: Match[]) => {
        if (pageId !== null && index >= 0) {
          const pageSlicers = this.getDataSlicersDimensions(pageId);
          if (pageSlicers?.[index]) {
            pageSlicers[index].matches = matches;
            pageSlicers[index].loading = false;
            this.setDataSlicersDimensions(pageId, pageSlicers);
          }
        }
      })
    ).subscribe();
  }

  public loadDataSlicersSchemas(page: number, mergedDatasets: any[], shareId?: string): void {
    this.dataSlicersViewData = [];
    const dataSlicers = this.getDataSlice(page);

    if (!dataSlicers) {
      this.dataSlicersViewDataSubject.next({ pageId: null, dataSlicers: [] });
      return;
    }

    const schemaRequests = dataSlicers?.map(slice => {
      const params = {};
      if (this.sessionService.publicId) {
        params['public_id'] = this.sessionService.publicId;
      }
      if (shareId) {
        params['share_id'] = shareId;
      }
      return this.analyticsApiService.loadSchema(slice.dataset, { cache: true, skipLoader: true, params: params }).pipe(
        tap(schema => {
          this.dataSlicersSchemas[slice.dataset] = schema;
        }),
      );
    });

    forkJoin(schemaRequests).pipe(take(1)).subscribe(() => {
      this.processSlicersViewData(page, mergedDatasets);
    });
  }

  public hasDataSlicers(dashboard: Dashboard, currentPageId: number): boolean {
    return dashboard.dataSlicers
      .filter(dataSlicer => !dataSlicer._destroy)
      .some(dataSlicer =>  dataSlicer.page === currentPageId);
  }

  public initializeDataSliceOptions() {
    return this.dataSlicersViewData$
      .pipe(
        tap(({ pageId, dataSlicers }) => {
          if (this.dataSlicersForm.length > 0) {
            this.dataSlicersForm.clear();
          }

          const existingPageData = this.getDataSlicersDimensions(pageId) || [];
          if (existingPageData.length !== dataSlicers.length) {
            this.setDataSlicersDimensions(pageId, []);
          }
        }),
        mergeMap(({ pageId, dataSlicers }: DataSlicersViewData) =>
          dataSlicers.map((slice) => {
            const control = this.fb.control([]);
            this.dataSlicersForm.push(control);

            const pageData = this.getDataSlicersDimensions(pageId);
            if (!pageData.some(existing => existing.id === slice.id)) {
              pageData.push(slice);
            }

            return control.valueChanges.pipe(
              map((selectedValues: Match[]) => ({
                slice,
                selectedValues,
              })),
            );
          }),
        ),
        mergeAll(),
      );
  }

  public setSelectionsForm(currentPageId: number, newPageId: number, mergedDatasets: any[], shareId?: string): void {
    const currentPageSelections = this.dataSlicersForm.getRawValue();
    if (currentPageSelections.length) {
      this.setDataSlicersSelections(currentPageId, currentPageSelections);
    }

    this.loadDataSlicersSchemas(newPageId, mergedDatasets, shareId);
    const nextPageSelections = this.getDataSlicersSelections(newPageId) || [];
    nextPageSelections.forEach((value, index) => {
      if (this.dataSlicersForm.controls[index]) {
        this.dataSlicersForm.controls[index].setValue(value, { emitEvent: true });
      }
    });
  }

  public setDataSliceOptions(
    selectedSliceData: DataSliceViewer,
    index: number,
    pageId: number,
    shareId?: string
  ): void {
    if (!this.loadedDataSlicersDropdowns.has(pageId)) {
      this.loadedDataSlicersDropdowns.set(pageId, new Set());
    }

    const pageDropdowns = this.loadedDataSlicersDropdowns.get(pageId);
    if (pageDropdowns.has(index)) {
      return;
    }

    this.fetchSliceOptions(selectedSliceData, index, pageId, shareId);
    const pageData = this.getDataSlicersDimensions(pageId);
    if (pageData?.[index]) {
      pageData[index].loading = true;
    }

    pageDropdowns.add(index);
  }

  private processSlicersViewData(page: number, mergedDatasets: Dataset[]): void {
    const sliceMap = new Map<number, DataSliceViewer>();
    const dataSlice = this.getDataSlice(page);

    dataSlice?.forEach((slice, sliceIndex) => {
      const mergedDataset = mergedDatasets.find(ds => ds.name === slice.dataset);
      if (!sliceMap.has(sliceIndex)) {
        sliceMap.set(sliceIndex,
          new DataSliceViewer({
            id: slice.id,
            dataset: mergedDataset,
            dimension: null,
            page: slice.page,
          }),
        );
      }

      const sliceData = sliceMap.get(sliceIndex);
      const schema = this.dataSlicersSchemas[slice.dataset];

      const dimension = schema.dimensions.find(dim => dim.name === slice.dimension);
      if (dimension) {
        sliceData.dimension = dimension;
        sliceData.title = `${mergedDataset.displayName} | ${dimension.displayName}`;
      }
    });

    this.dataSlicersViewData = Array.from(sliceMap.values());
    this.dataSlicersViewDataSubject.next({
      pageId: page,
      dataSlicers: this.dataSlicersViewData
    });
  }

  public saveCurrentFormSelections(pageId: number): void {
    if (this.dataSlicersForm.length > 0) {
      const currentSelections = this.dataSlicersForm.getRawValue();
      this.setDataSlicersSelections(pageId, currentSelections);
    }
  }

  public applySelectionsToForm(pageId: number): void {
    const selections = this.getDataSlicersSelections(pageId) || [];
    selections.forEach((value, index) => {
      if (index < this.dataSlicersForm.controls.length) {
        this.dataSlicersForm.controls[index].setValue(value, { emitEvent: false });
      }
    });
  }
}
